import { useEffect, useRef } from 'react';
import { readAlertSettings, useQueueAlertSettings } from './useQueueAlertSettings';

/**
 * Raises an alert when a queue count grows: a chime, a browser notification, and the count
 * in the tab title. Built for the inquiries queue, where a secretary is working in another
 * window and the sidebar badge alone is a signal nobody sees.
 *
 * Deliberately driven by a count rather than by events. The office has no push channel -
 * no SignalR anywhere in this codebase - so the queue is polled, and "it went up since the
 * last poll" is the only thing a poll can honestly tell us. A count that drops (someone
 * answered) is silent.
 */

/*
 * ⚠️ The keys are per scope. The office watches its queue depth and a coordinator watches their
 * own unread replies - two unrelated numbers. Sharing one key would have them overwrite each
 * other's baseline on every poll, and an admin who is also a coordinator (which is everyone in
 * testing) would get a chime on every alternate tick, or none at all.
 */
function lastSeenCountKey(scope: string): string {
  return `inquiries.alerts.${scope}.lastSeenCount`;
}

function lastSeenMessageKey(scope: string): string {
  return `inquiries.alerts.${scope}.lastSeenMessageAt`;
}

function readStoredString(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredString(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Falls back to in-memory behaviour for this session.
  }
}

/*
 * The last queue depth this browser was told about, kept across reloads.
 *
 * Without it the baseline lived in memory only, and every reload re-armed silently: an
 * inquiry that arrived while the page was reloading - or before the secretary opened the
 * tab at all - produced no chime, because the first poll after load simply accepted the
 * higher number as the new normal. Since a secretary reloads and reopens tabs all day,
 * that is most of the day.
 */
function readLastSeen(scope: string): number | null {
  const raw = readStoredString(lastSeenCountKey(scope));
  if (raw === null) return null;

  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}


/*
 * A generated chime rather than an audio file: two short notes, no asset to add to the
 * repo, no request that can 404 behind a CDN, and it cannot be silenced by a blocked
 * media fetch. WebAudio is in every browser this site supports.
 */
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;

  if (!audioContext) {
    try {
      audioContext = new Ctor();
    } catch {
      return null;
    }
  }
  return audioContext;
}

/**
 * Browsers refuse to start audio until the person has interacted with the page, and a
 * context created before that starts 'suspended'. This resumes it on the first click or
 * keypress, so the first real alert is audible instead of being the one that unlocks it.
 */
function installAudioUnlock(): () => void {
  const unlock = () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  };

  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });

  return () => {
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
}

/*
 * The loudest the chime goes, at volume 100. Kept well under 1: this fires while someone is
 * working in another window, and a startling alert gets switched off within a day.
 */
const MAX_CHIME_GAIN = 0.28;

/**
 * Plays the chime at the given volume (0–100). Exported so the settings panel can preview it:
 * a volume slider with nothing to listen to is a guess.
 */
/**
 * What the browser is currently willing to do with audio, for the settings panel to show.
 *
 * Worth surfacing rather than keeping to ourselves: "no sound" has three completely different
 * causes - the setting is off, the browser has not been interacted with yet, or WebAudio is
 * unavailable - and they are indistinguishable from the outside. Without this, diagnosing it
 * means reading someone else's devtools.
 */
export function getAudioStatus(): 'unavailable' | 'blocked' | 'ready' {
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return 'unavailable';
  if (!audioContext) return 'blocked';
  return audioContext.state === 'running' ? 'ready' : 'blocked';
}

/** When this browser last actually rang, so "I heard nothing" can be checked against it. */
let lastChimeAt: Date | null = null;

export function getLastChimeAt(): Date | null {
  return lastChimeAt;
}

/**
 * Plays the chime and measures the signal actually leaving the audio graph.
 *
 * ⚠️ Built because "the code ran but I heard nothing" has two completely different causes that
 * look identical from the outside: WebAudio producing silence, or WebAudio producing sound that
 * the machine is not playing - a muted tab (Chrome's own per-site mute), a muted system, or the
 * wrong output device. No amount of reading the code can tell them apart, and the person who
 * can hear the speakers is not the person who can read a stack trace.
 *
 * The analyser is inserted alongside the destination, so it measures the real output rather
 * than a separate test tone. A peak near zero means the graph is silent and the bug is ours; a
 * healthy peak with nothing audible means the sound is being produced and stopped downstream,
 * outside the page.
 *
 * Resolves with the peak amplitude, 0 to 1.
 */
export async function measureChimeOutput(volume: number): Promise<number> {
  const ctx = getAudioContext();
  if (!ctx) return 0;

  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return 0;
    }
  }

  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.connect(ctx.destination);

  playQueueChime(volume, analyser);

  const samples = new Float32Array(analyser.fftSize);
  let peak = 0;

  /*
   * Sampled over ~400ms: the chime is two notes 160ms apart, and a single reading right after
   * the call catches the envelope while it is still ramping up from zero.
   */
  const deadline = Date.now() + 400;
  while (Date.now() < deadline) {
    analyser.getFloatTimeDomainData(samples);
    for (const sample of samples) {
      const magnitude = Math.abs(sample);
      if (magnitude > peak) peak = magnitude;
    }
    await new Promise((resolve) => setTimeout(resolve, 25));
  }

  analyser.disconnect();
  return peak;
}

/**
 * @param extraDestination An additional node to route into, for measurement. Not for callers
 *   who just want a sound - see measureChimeOutput.
 */
export function playQueueChime(volume: number, extraDestination?: AudioNode): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  lastChimeAt = new Date();

  const level = Math.min(100, Math.max(0, volume)) / 100;
  if (level === 0) return;

  if (ctx.state === 'suspended') {
    // Resume is async; if it is still blocked the notes below are simply inaudible.
    void ctx.resume();
  }

  const peak = MAX_CHIME_GAIN * level;
  const now = ctx.currentTime;

  [
    { frequency: 880, at: 0 },
    { frequency: 1174, at: 0.16 },
  ].forEach(({ frequency, at }) => {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;

    // A short ramp at each end: a square-edged gain change clicks audibly.
    gain.gain.setValueAtTime(0, now + at);
    gain.gain.linearRampToValueAtTime(peak, now + at + 0.02);
    gain.gain.linearRampToValueAtTime(0, now + at + 0.22);

    oscillator.connect(gain);
    gain.connect(ctx.destination);
    if (extraDestination) gain.connect(extraDestination);
    oscillator.start(now + at);
    oscillator.stop(now + at + 0.24);
  });
}

/*
 * ============================================================
 *  צלצול בין חלונות
 * ============================================================
 * ⚠️ בלי זה מצלצל רק החלון שנמצא במיקוד, וזה נבדק ואומת. שתי סיבות מצטברות,
 * ושתיהן מחוץ לשליטתנו:
 *
 *   1. כרום **מדלל טיימרים בלשונית מוסתרת** לפעם בדקה, ולשונית שמוסתרת זמן
 *      רב הוא מקפיא לגמרי. כלומר הדגימה של 15 שניות פשוט לא רצה שם.
 *   2. refetchOnWindowFocus כבוי גלובלית בפרויקט, ולכן חזרה ללשונית גם לא
 *      מייצרת דגימה משלימה.
 *
 * הפתרון: הלשונית שכן ערה ומזהה - משדרת. כל השאר מצלצלות מיד, בלי להמתין
 * לדגימה משלהן. BroadcastChannel לא מחזיר את ההודעה למשדר עצמו, ולכן אין
 * כפילות ואין לופ.
 */
const CROSS_TAB_CHANNEL = 'inquiries-alert';

/*
 * A tab ignores a broadcast if it rang within this window. Two seconds covers the case that
 * matters - two alerts in one tab answering the same broadcast, and a tab that detected the
 * change itself being told about it by a sibling - without swallowing a genuine second message
 * moments later.
 */
const CROSS_TAB_DEDUPE_MS = 2000;

let broadcastChannel: BroadcastChannel | null = null;

function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;

  if (!broadcastChannel) {
    try {
      broadcastChannel = new BroadcastChannel(CROSS_TAB_CHANNEL);
    } catch {
      return null;
    }
  }
  return broadcastChannel;
}

interface CrossTabAlert {
  title: string;
  body: string;
}

/**
 * Rings in this tab: chime and notification, each subject to this person's settings.
 *
 * Shared by the tab that detected the change and by every tab told about it, so a broadcast
 * cannot produce a louder or quieter alert than a local one.
 */
function ringHere({ title, body }: CrossTabAlert): void {
  const { sound, volume, desktopNotification } = readAlertSettings();

  if (sound) playQueueChime(volume);
  if (desktopNotification) showBrowserNotification(title, body);
}

function showBrowserNotification(title: string, body: string): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  try {
    /*
     * A fixed tag so a quiet morning does not leave a stack of notifications: each new one
     * replaces the last, and the body carries the current total anyway.
     */
    const notification = new Notification(title, { body, tag: 'inquiries-queue' });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // Some browsers throw for a page-scoped Notification and want a service worker instead.
  }
}

/**
 * Asks for notification permission on the first interaction, once per browser. Asking on
 * mount instead gets an automatic refusal in browsers that require a gesture - and a
 * refusal is permanent, so it is worth waiting for a click.
 */
function installNotificationPermissionRequest(): () => void {
  if (!('Notification' in window) || Notification.permission !== 'default') return () => {};

  const ask = () => { void Notification.requestPermission(); };

  window.addEventListener('pointerdown', ask, { once: true });
  window.addEventListener('keydown', ask, { once: true });

  return () => {
    window.removeEventListener('pointerdown', ask);
    window.removeEventListener('keydown', ask);
  };
}

interface QueueAlertOptions {
  /** Current queue depth. Pass undefined while it is still unknown. */
  count: number | undefined;
  /**
   * When a coordinator last wrote. This, and not the count, is what says something arrived: a
   * reply on an inquiry that is still New moves no count at all.
   */
  lastUserMessageAt: string | null | undefined;
  /**
   * Which side is watching. Keys the stored baseline, so the office's queue depth and a
   * coordinator's unread replies never overwrite each other - the same person is usually both.
   */
  storageScope: 'office' | 'coordinator';
  /**
   * Whether this alert owns the tab title. Exactly one may: there is one document.title, and
   * two hooks writing it would overwrite each other's number on every poll. The office side
   * owns it, since a secretary working the queue is the one who needs it from another window.
   */
  ownsTabTitle: boolean;
  /** False switches the whole thing off - a user who cannot see the queue is not alerted about it. */
  enabled: boolean;
  notificationTitle: string;
  /** Given the count, so the notification can say how many are waiting. */
  buildNotificationBody: (count: number) => string;
}

export default function useQueueAlert({
  count, lastUserMessageAt, storageScope, ownsTabTitle, enabled, notificationTitle, buildNotificationBody,
}: QueueAlertOptions): void {
  /*
   * The count this browser last knew about. Seeded from localStorage so a reload does not
   * lose it; null only on the very first visit, and then the first poll arms quietly -
   * otherwise opening the screen would announce every inquiry already in the queue, which
   * on this installation is 269 of them.
   */
  const previousCountRef = useRef<number | null>(readLastSeen(storageScope));

  /* The newest coordinator message this browser has already announced. */
  const previousMessageAtRef = useRef<string | null>(readStoredString(lastSeenMessageKey(storageScope)));

  /*
   * Subscribed rather than read once, but only for the tab title: it is the one signal that is
   * always on screen, so switching it off has to take effect immediately rather than at the
   * next inquiry. The chime and the notification read their settings at the moment they fire.
   */
  const { settings } = useQueueAlertSettings();

  useEffect(() => {
    if (!enabled) return;
    const removeAudioUnlock = installAudioUnlock();
    const removePermissionRequest = installNotificationPermissionRequest();
    return () => {
      removeAudioUnlock();
      removePermissionRequest();
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || count === undefined || lastUserMessageAt === undefined) return;

    const previousCount = previousCountRef.current;
    const previousMessageAt = previousMessageAtRef.current;

    previousCountRef.current = count;
    previousMessageAtRef.current = lastUserMessageAt;
    writeStoredString(lastSeenCountKey(storageScope), String(count));
    if (lastUserMessageAt) writeStoredString(lastSeenMessageKey(storageScope), lastUserMessageAt);

    // First poll of a browser that has never seen this: arm quietly, announce nothing.
    if (previousCount === null && previousMessageAt === null) return;

    /*
     * Two independent signals, either of which means work arrived:
     *
     *   - a newer coordinator message than this browser has seen. This is the one that
     *     matters most and the one that was missing: a reply on an inquiry that is still New
     *     changes no count, so watching the count alone stayed silent while a message waited.
     *   - a queue that grew. Kept as well, since an inquiry can be reopened or moved back to
     *     the office without a new message being written.
     */
    const hasNewerMessage = !!lastUserMessageAt
      && (!previousMessageAt || new Date(lastUserMessageAt) > new Date(previousMessageAt));
    const queueGrew = previousCount !== null && count > previousCount;

    if (!hasNewerMessage && !queueGrew) return;

    /*
     * The settings are read inside ringHere, at the moment of the alert, rather than through
     * this hook's dependencies: they must not be able to re-run this effect, or turning the
     * chime off would itself look like a change and could sound one last time.
     */
    const alert = { title: notificationTitle, body: buildNotificationBody(count) };

    ringHere(alert);

    // And every other tab of this browser, which may be throttled and not have noticed yet.
    getBroadcastChannel()?.postMessage(alert);
  }, [count, lastUserMessageAt, storageScope, enabled, notificationTitle, buildNotificationBody]);

  /*
   * Rings when another tab did the detecting.
   *
   * ⚠️ Deduped by time, not by which alert listens. Both alerts can be mounted in one tab (an
   * admin who is also a coordinator - everyone, in testing), and both would answer the same
   * broadcast: one message, two chimes. Gating on "the office one listens" was the first
   * attempt and was worse - a coordinator-only window has no office alert, so it would have
   * heard nothing at all from other tabs.
   */
  useEffect(() => {
    if (!enabled) return;

    const channel = getBroadcastChannel();
    if (!channel) return;

    const onMessage = (event: MessageEvent<CrossTabAlert>) => {
      if (!event.data?.title) return;

      const sinceLast = lastChimeAt ? Date.now() - lastChimeAt.getTime() : Infinity;
      if (sinceLast < CROSS_TAB_DEDUPE_MS) return;

      ringHere(event.data);
    };

    channel.addEventListener('message', onMessage);
    return () => channel.removeEventListener('message', onMessage);
  }, [enabled]);

  /*
   * The count in the tab title, which is the one signal that survives the window being
   * behind another one and needs no permission from anybody.
   */
  const tabTitleEnabled = ownsTabTitle && settings.tabTitle;

  useEffect(() => {
    if (!enabled) return;

    /*
     * The base title is read here rather than captured once at mount. App sets the site
     * title in an effect of its own, and mount order is not ours to rely on - capturing
     * early pinned index.html's placeholder and the tab kept showing it forever. Any
     * count this hook already prefixed is stripped, so repeated runs cannot stack up
     * "(9) (8) title".
     */
    const base = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = tabTitleEnabled && count && count > 0 ? `(${count}) ${base}` : base;

    return () => { document.title = base; };
  }, [count, enabled, tabTitleEnabled]);
}
